// ConsistencyRing — 0–100% habit-strength curve. Replaces StreakRing
// as the primary "how am I doing" visual.
//
// Why this exists: the prior streak counter resets to 0 on a bad day,
// which the design research flagged as a validated anti-pattern in
// recovery audiences ("you lost it" copy = shame trigger). The
// consistency curve decays gracefully — a single bad day shaves a few
// points, no reset, no "broken" state.
//
// Visual: a soft arc inside a calm background ring, integer percent
// ticking up via framer-motion, freeze count quiet underneath. Click
// to spend a freeze when the user is over today's target.

import { motion } from 'framer-motion';
import { Snowflake } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { Skeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { useTickUp, usePrev, useReducedMotion, EASE_EMPHASIS } from '../../lib/motion';
import type { StreakInfo } from '@shared/types';

interface ConsistencyRingProps {
  streak: StreakInfo | null;
  loading?: boolean;
  /** Show a "Protect today" button if non-null. Wires to /api/me/use-streak-freeze. */
  onProtectToday?: () => void;
  /** Disable the protect button (e.g., already used within 24h). */
  protectBusy?: boolean;
  /** Compact mode (no protect button, no day count). For tight slots. */
  compact?: boolean;
  size?: number;
  /**
   * Phase B §P10.6 — honest-day suppression. When true, the
   * framer-motion celebratory pulse on increment is skipped. The
   * ring + score still render normally (the data isn't hidden);
   * we just don't celebrate ticking up on a day the user went
   * over target.
   */
  suppressCelebration?: boolean;
}

export function ConsistencyRing({
  streak,
  loading,
  onProtectToday,
  protectBusy,
  compact = false,
  size = 96,
  suppressCelebration = false,
}: ConsistencyRingProps) {
  const reduce = useReducedMotion();
  const score = streak?.consistencyScore ?? 100;
  const animated = useTickUp(score, { durationMs: 700, ease: 'emphasis' });
  const prev = usePrev(score);
  const increased = prev != null && score > prev;
  const shouldPulse = increased && !reduce && !suppressCelebration;

  if (loading || !streak) {
    return <Skeleton width={size} height={size} radius="50%" />;
  }

  // Arc math: full circumference, fill 0–100% as stroke-dashoffset.
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, animated / 100));
  const offset = c * (1 - pct);

  return (
    <div className="consistency-ring-wrap">
      <motion.div
        className="consistency-ring"
        style={{ inlineSize: size, blockSize: size }}
        animate={shouldPulse ? { scale: [1, 1.04, 1] } : { scale: 1 }}
        transition={{ duration: 0.36, ease: EASE_EMPHASIS }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="ring-bg" />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeWidth={stroke}
            className="ring-fg"
            strokeDasharray={c}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <div className="consistency-ring-text">
          <span className="consistency-ring-value">{Math.round(animated)}</span>
          <span className="consistency-ring-percent">%</span>
        </div>
      </motion.div>
      {!compact ? (
        <div className="consistency-ring-meta">
          <span className="consistency-ring-label">consistency</span>
          <span className="consistency-ring-freezes" title={`${streak.freezesRemaining} freezes remaining`}>
            <Icon icon={Snowflake} size={12} />
            {streak.freezesRemaining}
          </span>
        </div>
      ) : null}
      {!compact && onProtectToday && streak.freezesRemaining > 0 ? (
        <Button
          variant="tertiary"
          size="md"
          onClick={onProtectToday}
          loading={protectBusy}
          className="consistency-ring-protect"
        >
          Protect today
        </Button>
      ) : null}
    </div>
  );
}
